'use client';

import { faCalendar } from '@fortawesome/free-regular-svg-icons';
import { faEnvelope, faPhone } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Badge from '@ui/Badge';
import { Button } from '@ui/Button';
import { easeInOut, motion } from 'framer-motion';
import React, { PropsWithChildren } from 'react';
import { cn } from '~/lib/cn';
import Highlighter from '../../AnimatedComponents/Highlighter';

interface Props {
    badge?: string;
    className?: string;
}

const containerVariants = {
    hidden: { opacity: 0, y: 24 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.5,
            ease: easeInOut,
            staggerChildren: 0.1
        }
    }
};

const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.4,
            ease: easeInOut
        }
    }
};

export const PriceSection: React.FC<PropsWithChildren<Props>> = ({ children, badge, className }) => {
    return (
        <section className={cn('tw-w-full', className)}>
            <motion.div
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ amount: 0.1, once: true }}
                className="tw-space-y-8"
            >
                <motion.div
                    initial="rest"
                    whileHover="hover"
                    className="tw-card-bordered tw-relative tw-overflow-hidden tw-rounded-xl tw-border-l-8 tw-border-l-success/70 tw-p-6 md:tw-p-10"
                >
                    <motion.div
                        variants={itemVariants}
                        className="tw-flex tw-flex-wrap tw-items-start tw-justify-between tw-gap-4"
                    >
                        <div>
                            <p className="tw-mb-2 tw-text-xs tw-font-bold tw-uppercase tw-tracking-[0.2em] tw-text-success">
                                Dein Angebot
                            </p>
                            <h2 className="tw-m-0 tw-text-3xl tw-font-bold tw-text-accents-9">
                                <Highlighter strokeWidth={2} iterations={3} variant="successLight" multiline>
                                    Preise & Konditionen
                                </Highlighter>
                            </h2>
                        </div>

                        {badge && (
                            <motion.div
                                initial={{ scale: 0.9, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ delay: 0.2 }}
                                className="tw-hidden md:tw-block"
                            >
                                <Badge flat size="xl" color="success">
                                    {badge}
                                </Badge>
                            </motion.div>
                        )}
                    </motion.div>

                    <motion.div
                        variants={itemVariants}
                        className="tw-my-8 tw-text-accents-7 [&_li]:tw-mb-2 [&_ul]:tw-pl-5"
                    >
                        {children}
                    </motion.div>

                    <motion.div
                        variants={itemVariants}
                        className="tw-card-bordered tw-overflow-hidden tw-rounded-xl tw-bg-gradient-to-r tw-from-white tw-to-success/5 tw-p-8"
                    >
                        <div className="tw-relative">
                            <div className="tw-relative tw-z-10">
                                <div className="tw-flex tw-items-start tw-gap-4">
                                    <div className="tw-hidden tw-flex-shrink-0 tw-rounded-lg tw-bg-success/10 tw-p-2 md:tw-block">
                                        <svg
                                            className="tw-h-6 tw-w-6 tw-text-success"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                            />
                                        </svg>
                                    </div>
                                    <div>
                                        <h5 className="tw-mb-2 tw-font-bold tw-text-accents-9">
                                            Möchtest du mehr erfahren?
                                        </h5>
                                        <p className="tw-mb-6 tw-text-sm tw-leading-relaxed tw-text-accents-6">
                                            Dann kontaktiere uns jetzt für ein unverbindlichen Termin, in welchem wir
                                            genau auf deine Bedürfnisse eingehen und dir ein maßgeschneidertes Angebot
                                            erstellen können.
                                        </p>
                                    </div>
                                </div>

                                <div className="tw-flex tw-flex-col tw-gap-4 md:tw-flex-row">
                                    <Button.Link
                                        size="sm"
                                        href="https://calendly.com/sportision-team/beratung-zur-digitalisierung-mit-sportision"
                                    >
                                        <FontAwesomeIcon icon={faCalendar} className="tw-mr-2" />
                                        Jetzt Termin buchen
                                    </Button.Link>
                                    <Button.Link size="sm" flat href="tel:029228751440">
                                        <FontAwesomeIcon icon={faPhone} className="tw-mr-2" />
                                        Jetzt anrufen
                                    </Button.Link>

                                    <Button.Link size="sm" flat href="mailto:sales@sportision.de">
                                        <FontAwesomeIcon icon={faEnvelope} className="tw-mr-2" />
                                        E-Mail schreiben
                                    </Button.Link>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            </motion.div>
        </section>
    );
};
